import React, { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import {
    Package,
    Box,
    Flame,
    Scan,
    Layers,
    Zap,
    Sliders,
    CheckCircle,
    Sparkles,
    Eye,
    Workflow,
    Image as ImageIcon
} from 'lucide-react';

const productServices = [
    { id: '01', icon: Box, title: 'Clean E-Commerce Packshots', desc: 'Flawlessly isolated, shadow-controlled product shots on pure white or custom brand backdrops designed natively for Amazon, Shopify, and global apps.' },
    { id: '02', icon: Flame, title: 'Creative Commercial Campaigns', desc: 'High-concept, narrative-driven art direction featuring dynamic liquid splashes, complex levitation geometry, and specialized lighting props.' },
    { id: '03', icon: Scan, title: 'Macro Detail & Jewelry Masters', desc: 'Extreme close-up precision utilizing advanced focus-stacking to lock crisp, endless sharp depth across microscopic textures, stones, and tech facets.' },
    { id: '04', icon: Package, title: 'Cosmetics & Texture Curation', desc: 'Styling thick creams, rich serums, and powders with precision palette knives and macro lighting arrays to amplify physical product touch and feel.' },
    { id: '05', icon: Sparkles, title: 'Luxury Lifestyle Contextuals', desc: 'Placing your physical product in curated premium spaces, blending architectural natural light with studio strobes to evoke authentic consumer desire.' },
    { id: '06', icon: Zap, title: 'High-Speed Kinetic Captures', desc: 'Stopping time perfectly at 1/8000th of a second to lock shattering glass, flying dust particles, falling coffee beans, or exploding carbonation.' },
];

const productDeliverables = [
    { id: '01', icon: CheckCircle, title: 'Delta-E Strict Color Matching', desc: 'Calibrating sessions with strict physical color charts to ensure physical product fabrics, dyes, and plastics match digital screens perfectly.' },
    { id: '02', icon: Layers, title: 'Focus-Stacked Micro Depth', desc: 'Merging dozens of separate exposures at slightly varied focal paths to maintain absolute front-to-back sharpness across complex 3D objects.' },
    { id: '03', icon: Sliders, title: 'Non-Destructive Alpha Clipping', desc: 'Delivering beautifully hand-drawn pen-to-path clipping selections and transparent alpha masks for seamless multichannel graphic design.' },
    { id: '04', icon: Workflow, title: 'Ghost Mannequin & Apparel Formats', desc: 'Combining multiple inner/outer exposures of apparel to create an organic, structured, invisible three-dimensional body configuration.' },
    { id: '05', icon: Eye, title: 'Precision Polarization Control', desc: 'Deploying linear cross-polarizing studio films to completely eliminate unwanted blinding glares on reflective chrome, plastic, or glassware.' },
    { id: '06', icon: Box, title: 'Batch Metadata Web Optimization', desc: 'Pre-flight file compressing, tagging, and renaming optimized specifically to boost SEO visibility across web searches and marketplaces.' },
    { id: '07', icon: Sparkles, title: 'Flawless Digital Dust Extraction', desc: 'Microscopic retouching tracking down every stray dust fiber, pixel aberration, or manufacturing blemish to maintain premium value.' },
    { id: '08', icon: Package, title: 'Packaging Geometry Correction', desc: 'Digital focal adjustments that undo natural wide-angle barrel distortions, keeping box parallel lines perfectly perpendicular and sharp.' },
    { id: '09', icon: ImageIcon, title: 'Redundant Ultra-Res File Archives', desc: 'Securing massive raw and layered uncompressed master assets inside highly secure redundant servers for future print billboard formatting.' },
];

const ProductPhotographyPortfolio = () => {
    const viewportRef = useRef(null);
    const leftStripRef = useRef(null);
    const rightStripRef = useRef(null);

    useEffect(() => {
        const viewport = viewportRef.current;
        const leftStrip = leftStripRef.current;
        const rightStrip = rightStripRef.current;

        if (!viewport || !leftStrip || !rightStrip) return;

        let currentPosLeft = 0;
        let currentPosRight = 0;
        let isHovered = false;
        let animationFrameId;

        const speedLeft = 0.48;
        const speedRight = 0.28;

        const handleMouseEnter = () => { isHovered = true; };
        const handleMouseLeave = () => { isHovered = false; };

        viewport.addEventListener('mouseenter', handleMouseEnter);
        viewport.addEventListener('mouseleave', handleMouseLeave);

        function performAutoScroll() {
            if (!isHovered && leftStrip && rightStrip) {
                currentPosLeft -= speedLeft;
                currentPosRight -= speedRight;

                if (Math.abs(currentPosLeft) >= (leftStrip.scrollHeight / 2)) {
                    currentPosLeft = 0;
                }
                if (Math.abs(currentPosRight) >= (rightStrip.scrollHeight / 2)) {
                    currentPosRight = 0;
                }

                if (window.innerWidth > 991) {
                    leftStrip.style.transform = `translateY(${currentPosLeft}px)`;
                    rightStrip.style.transform = `translateY(${currentPosRight}px)`;
                } else {
                    leftStrip.style.transform = 'none';
                    rightStrip.style.transform = 'none';
                }
            }
            animationFrameId = requestAnimationFrame(performAutoScroll);
        }

        animationFrameId = requestAnimationFrame(performAutoScroll);

        return () => {
            cancelAnimationFrame(animationFrameId);
            if (viewport) {
                viewport.removeEventListener('mouseenter', handleMouseEnter);
                viewport.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, []);

    return (
        <div>
            <Helmet>
                <title>Product Photography Portfolio in Hyderabad | KhultStudios</title>

                <meta
                    name="description"
                    content="Explore KhultStudios' Product Photography Portfolio featuring premium eCommerce product photography, Amazon & Flipkart listing images, lifestyle product photography, catalog photography, advertising campaigns, and commercial brand photography in Hyderabad."
                />

                <meta
                    name="keywords"
                    content="Product Photography Portfolio Hyderabad, Product Photography Hyderabad, eCommerce Product Photography, Amazon Product Photography, Flipkart Product Photography, Catalog Photography, Lifestyle Product Photography, Commercial Product Photography, Brand Photography Hyderabad, Advertising Photography, Jewellery Product Photography, Cosmetic Product Photography, Food Product Photography, Apparel Photography, Product Photographer Hyderabad, KhultStudios"
                />

                <meta name="robots" content="index, follow" />
                <meta name="author" content="KhultStudios" />

                <link
                    rel="canonical"
                    href="https://khultstudios.com/product-photography-portfolio"
                />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta
                    property="og:url"
                    content="https://khultstudios.com/product-photography-portfolio"
                />
                <meta
                    property="og:title"
                    content="Product Photography Portfolio in Hyderabad | KhultStudios"
                />
                <meta
                    property="og:description"
                    content="Browse our portfolio of professional product photography for eCommerce, Amazon, Flipkart, catalogs, advertising, websites, and brand campaigns."
                />
                <meta
                    property="og:image"
                    content="https://khultstudios.com/images/product-photography-portfolio-banner.jpg"
                />
                <meta property="og:site_name" content="KhultStudios" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content="Product Photography Portfolio | KhultStudios"
                />
                <meta
                    name="twitter:description"
                    content="Explore premium product photography portfolio for eCommerce, catalogs, advertising, and branding in Hyderabad."
                />
                <meta
                    name="twitter:image"
                    content="https://khultstudios.com/images/product-photography-portfolio-banner.jpg"
                />

                {/* Favicon */}
                <link rel="icon" href="https://khultstudios.com/favicon.ico" />
            </Helmet>
            {/* FULLSCREEN VIDEO VIEWPORT BACKGROUND ENGINE */}
            <div className="video-bg-viewport">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="global-html5-video"
                >
                    <source src="videos/product_macro_reel.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
                <div className="vignette-overlay-layer" />
            </div>

            {/* INNER APP CANVAS CONTAINER SECTION */}
            <div className="page-frame px-2 px-md-5 pb-5">
                <div className="landing-container position-relative overflow-hidden">
                    <div className="paper-texture" />
                    <div className="container-fluid h-100 p-4 p-lg-5 position-relative z-2">
                        <div className="row h-100 gy-5">

                            {/* Content Brief side description */}
                            <div className="col-lg-5 d-flex flex-column justify-content-center h-100 content-side">
                                <div className="invitation-text position-relative py-4">
                                    <h5 className="text-uppercase tracking-wider text-orange fw-bold mb-2">
                                        Commercial & E-Commerce Asset Architects
                                    </h5>
                                    <h1 className="display-3 text-uppercase main-title text-white mb-4">
                                        Visual Assets That Drive Conversion
                                    </h1>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        In the digital marketplace, your consumer cannot touch, hold, or physically interact with your product. Your imagery carries the entire burden of perceived quality. Stale, improperly lit, or color-inaccurate captures degrade your brand equity instantly.
                                    </p>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        At KhultStudio, we build elite, conversion-focused commercial imagery. By leveraging cross-polarized glare eradication, automated micro focus-stacking, and multi-exposure compositing, we craft product assets that command customer trust and lift marketplace sales.
                                    </p>
                                    <a
                                        href="#details"
                                        className="btn btn-orange text-uppercase px-4 py-2 text-dark fw-bold tracking-wide btn-scroll"
                                    >
                                        Explore Work
                                    </a>
                                </div>
                                <div className="social-footer d-flex gap-3 mt-2">
                                    <a href="#" className="social-icon"><i className="fab fa-facebook-f" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-instagram" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-pinterest" /></a>
                                </div>
                            </div>

                            {/* Infinite Slanted Ribbon Gallery Strip components */}
                            <div className="col-lg-7 position-relative photo-zone-height">
                                <div className="filmstrip-skew-box">
                                    <div
                                        className="vertical-scroll-viewport"
                                        ref={viewportRef}
                                    >
                                        <div className="row gx-4 h-100">
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon" ref={leftStripRef} style={{ display: 'none' }}>
                                                    <div className="ribbon-cell">
                                                        <img src="/product-photography/product-photography.jpg" alt="Wedding 5" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/product-photography/product-photography1.jpg" alt="Wedding 6" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/product-photography/product-photography2.jpg" alt="Wedding 7" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/product-photography/mproduct-photography3.jpg" alt="Wedding 8" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="/product-photography/product-photography.jpg" alt="Wedding 5 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/product-photography/product-photography1.jpg" alt="Wedding 6 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/product-photography/product-photography2.jpg" alt="Wedding 7 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/product-photography/product-photography3.jpg" alt="Wedding 8 Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon ribbon-offset" ref={rightStripRef}>
                                                    <div className="ribbon-cell">
                                                        <img src="/product-photography/product-photography.jpg" alt="Wedding 5" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/product-photography/product-photography1.jpg" alt="Wedding 6" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/product-photography/product-photography2.jpg" alt="Wedding 7" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/product-photography/product-photography3.jpg" alt="Wedding 8" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="/product-photography/product-photography.jpg" alt="Wedding 5 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/product-photography/product-photography1.jpg" alt="Wedding 6 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/product-photography/product-photography2.jpg" alt="Wedding 7 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/product-photography/product-photography3.jpg" alt="Wedding 8 Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* COMMERCIAL PRODUCT PORTFOLIO SERVICES SECTION */}
            <section className="services-section py-5" id="details">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">📦 Commercial Product Capture Classifications</h2>
                    <p className="text-light-gray mt-3 text-center max-w-2xl mx-auto">
                        Different marketing layers require distinct visual engineering. Our structured photography configurations align directly with technical marketplace standards and high-concept advertising grids.
                    </p>
                    <div className="row g-4 mt-2">
                        {productServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className={`service-card ${index === 2 ? 'active' : ''}`}>
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h3>{service.title}</h3>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PRODUCT TECHNICAL REASSURANCE & PROCESS SECTION */}
            <section className="comprehensive-section py-5 bg-black-opacity-50">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">⚙️ Technical Execution & Commercial Deliverable Pipelines</h2>
                    <div className="row g-4">
                        {productDeliverables.map((service, index) => (
                            <div key={index} className="col-lg-4 col-md-6">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ProductPhotographyPortfolio;